// Colectas fijas: las rondas que salen todos los días a la misma hora y en
// las que lo único que cambia es el chofer. Cada noche el servidor genera los
// recorridos reales del día siguiente desde acá y le avisa al chofer; acá se
// arman y editan las rondas, y se ve quién confirmó.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type Store = { id: number; nombre_fantasia: string; direccion?: string | null; codigo_postal?: string | number | null };
type Driver = { id: number; username: string; first_name: string | null; last_name: string | null };
type Departure = { id?: number; departure_time: string; weekdays: number[]; window_seq: number; default_driver_id: number | null; default_driver_username?: string | null; active: boolean };
type TemplateStop = { sequence: number; store_id: number; store_name: string; store_address?: string | null; postal_code?: string | null };
type Template = { id: number; name: string; notes: string | null; active: boolean; stops: TemplateStop[]; departures: Departure[] };
type GeneratedRoute = { id: number; status: string; scheduled_date: string; driver_id: number | null; driver_username: string | null; template_id: number | null; template_name: string | null; departure_time: string | null; stops_count: number; confirmation_state: "ACCEPTED" | "DECLINED" | "PENDING" | "OVERDUE" | null };

const WEEKDAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
// Misma regla que en Tiendas: lunes a viernes se configuran como un solo
// grupo. weekdays sigue siendo un array libre (soporta cualquier combinación),
// esto es sólo el selector que se ofrece para cargarlo.
const DAY_GROUPS: { key: string; label: string; days: readonly number[] }[] = [
  { key: "weekdays", label: "Lun-Vie", days: [1, 2, 3, 4, 5] },
  { key: "saturday", label: "Sáb", days: [6] },
  { key: "sunday", label: "Dom", days: [0] },
];
const hhmm = (t: string | null | undefined) => (t ? t.slice(0, 5) : "—");
const driverName = (d: { username: string; first_name?: string | null; last_name?: string | null } | null | undefined) =>
  d ? [d.first_name, d.last_name].filter(Boolean).join(" ") || d.username : "—";
const isoPlus = (days: number) => { const d = new Date(); d.setDate(d.getDate() + days); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

const CONFIRMATION: Record<string, { label: string; color: string }> = {
  ACCEPTED: { label: "Confirmó", color: "var(--success)" },
  DECLINED: { label: "No puede", color: "var(--danger)" },
  PENDING: { label: "Sin confirmar", color: "var(--warning, #d29922)" },
  OVERDUE: { label: "Sin confirmar · vencido", color: "var(--danger)" },
};

const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "top" };
const selectStyle: React.CSSProperties = { width: "100%", minHeight: "48px", padding: "0 14px", borderRadius: "16px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)" };
const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const badge = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, border: `1px solid ${color}`, color });
const chip = (on: boolean): React.CSSProperties => ({ padding: "4px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, cursor: "pointer", border: `1px solid ${on ? "var(--primary, #4f8cff)" : "var(--border)"}`, color: on ? "var(--primary, #4f8cff)" : "var(--text-secondary)", background: "transparent" });

function Modal({ title, onClose, children, width = "960px" }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; width?: string }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", zIndex: 100, overflowY: "auto" }}>
      {/* Sobre Card, como las ventanas de Estanterías y Planta: la variable
          --surface del tema oscuro es blanco al 3 % y usada como fondo propio
          dejaba ver la pantalla de atrás. */}
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: width }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
            <h3 style={{ margin: 0, fontSize: "18px", color: "var(--text-primary)" }}>{title}</h3>
            <Button variant="secondary" onClick={onClose}>Cerrar</Button>
          </div>
          {children}
        </Card>
      </div>
    </div>
  );
}

export default function ColectasFijasPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const req = useCallback(async (path: string, init?: RequestInit) => {
    // json: true pone la cabecera Content-Type; sin ella Express no parsea el
    // cuerpo y todo POST/PUT de esta pantalla llegaba vacio («name requerido»).
    const res = await authFetch(path, { ...(init ?? {}), token, json: init?.body !== undefined });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.ok === false) throw new Error(json?.error || `Error ${res.status}`);
    return json;
  }, [token]);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [generated, setGenerated] = useState<GeneratedRoute[]>([]);
  const [genDate, setGenDate] = useState(isoPlus(1));
  const [editing, setEditing] = useState<Template | "new" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const flash = (m: string) => { setNotice(m); window.setTimeout(() => setNotice(null), 4000); };

  const loadTemplates = useCallback(async () => {
    try { setTemplates((await req("/v2/route-templates?all=1")).templates ?? []); } catch (e: any) { setError(e.message); }
  }, [req]);
  const loadGenerated = useCallback(async () => {
    try { setGenerated(((await req(`/v2/routes?date=${genDate}&type=PICKUP`)).routes ?? []).filter((r: GeneratedRoute) => r.template_id)); } catch (e: any) { setError(e.message); }
  }, [req, genDate]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => { loadGenerated(); }, [loadGenerated]);
  useEffect(() => {
    (async () => {
      try {
        const [s, d] = await Promise.all([req("/v2/stores"), req("/v2/routes/drivers")]);
        setStores((s.stores ?? []).map((x: any) => ({ id: Number(x.id), nombre_fantasia: x.nombre_fantasia ?? `Tienda ${x.id}`, direccion: x.direccion, codigo_postal: x.codigo_postal })));
        setDrivers(d.drivers ?? []);
      } catch (e: any) { setError(e.message); }
    })();
  }, [req]);

  const generateNow = async () => {
    setBusy(true);
    try {
      const r = await req("/v2/route-templates/generate", { method: "POST", body: JSON.stringify({ date: genDate }) });
      flash(`${r.created.length} recorridos generados para ${genDate} (${r.skipped} ya existían) · ${r.notified} choferes avisados`);
      loadGenerated();
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };
  const assign = async (routeId: number, driverId: string) => {
    try {
      await req(`/v2/routes/${routeId}/driver`, { method: "PUT", body: JSON.stringify({ driver_id: driverId ? Number(driverId) : null }) });
      flash(driverId ? "Chofer asignado; se le avisa a la noche o al generar" : "Chofer quitado");
      loadGenerated();
    } catch (e: any) { setError(e.message); }
  };

  const active = templates.filter((t) => t.active), inactive = templates.filter((t) => !t.active);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Colectas fijas</h2>
            <p style={subtitleStyle}>Las rondas que salen siempre igual: mismas tiendas, misma hora, los días que corresponda. Cada noche a las 20 se generan los recorridos de mañana y se le avisa al chofer; lo único que queda es asignar o cambiar choferes.</p>
          </div>
          <Button onClick={() => setEditing("new")}>Nueva ronda</Button>
        </div>
      </Card>

      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)" }}>{error} <Button variant="secondary" onClick={() => setError(null)}>Cerrar</Button></div> : null}
      {notice ? <div style={{ ...panelStyle, borderColor: "var(--success)", color: "var(--success)" }}>{notice}</div> : null}

      <Card>
        <h3 style={{ margin: "0 0 12px", fontSize: "16px", color: "var(--text-primary)" }}>Rondas <span style={smallMutedStyle}>· {active.length} activas</span></h3>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Ronda</th><th style={thStyle}>Tiendas</th><th style={thStyle}>Salidas</th><th style={thStyle}></th></tr></thead>
            <tbody>
              {templates.length === 0 ? <tr><td style={tdStyle} colSpan={4}><span style={smallMutedStyle}>Todavía no hay rondas fijas. Creá la primera con «Nueva ronda».</span></td></tr> : null}
              {[...active, ...inactive].map((t) => (
                <tr key={t.id} style={t.active ? undefined : { opacity: 0.55 }}>
                  <td style={tdStyle}><b>{t.name}</b>{!t.active ? <span style={{ ...badge("var(--text-secondary)"), marginLeft: 8 }}>inactiva</span> : null}{t.notes ? <div style={smallMutedStyle}>{t.notes}</div> : null}</td>
                  <td style={tdStyle}>{t.stops.length} <span style={smallMutedStyle}>· {t.stops.slice(0, 4).map((s) => s.store_name).join(", ")}{t.stops.length > 4 ? "…" : ""}</span></td>
                  <td style={tdStyle}>
                    {t.departures.map((d) => (
                      <div key={d.id ?? d.departure_time} style={{ marginBottom: 4 }}>
                        <b>{hhmm(d.departure_time)}</b> <span style={smallMutedStyle}>{d.weekdays.map((w) => WEEKDAYS[w]).join(" ")} · pasada {d.window_seq} · {d.default_driver_username ? `chofer habitual ${d.default_driver_username}` : "sin chofer habitual"}</span>
                      </div>
                    ))}
                  </td>
                  <td style={tdStyle}><Button variant="secondary" onClick={() => setEditing(t)}>Editar</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "end", marginBottom: "12px" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "16px", color: "var(--text-primary)" }}>Recorridos generados</h3>
            <p style={{ ...smallMutedStyle, margin: "4px 0 0" }}>Lo que salió de las rondas para esa fecha, con la respuesta de cada chofer. Un chofer que no contestó sigue asignado: se puede cambiar acá mismo.</p>
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "end", flexWrap: "wrap" }}>
            <Input label="Fecha" type="date" value={genDate} onChange={(e) => setGenDate(e.target.value || isoPlus(1))} />
            <Button variant="secondary" onClick={loadGenerated}>Actualizar</Button>
            <Button onClick={generateNow} disabled={busy}>{busy ? "Generando…" : "Generar ahora y avisar"}</Button>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Salida</th><th style={thStyle}>Ronda</th><th style={thStyle}>Estado</th><th style={thStyle}>Chofer</th><th style={thStyle}>Confirmación</th><th style={thStyle}></th></tr></thead>
            <tbody>
              {generated.length === 0 ? <tr><td style={tdStyle} colSpan={6}><span style={smallMutedStyle}>Nada generado para {genDate}. Se genera solo a las 20 del día anterior, o ahora con el botón.</span></td></tr> : null}
              {generated.map((r) => {
                const c = r.confirmation_state ? CONFIRMATION[r.confirmation_state] : null;
                return (
                  <tr key={r.id}>
                    <td style={tdStyle}><b>{hhmm(r.departure_time)}</b></td>
                    <td style={tdStyle}>{r.template_name} <span style={smallMutedStyle}>· #{r.id} · {r.stops_count} tiendas</span></td>
                    <td style={tdStyle}><span style={badge("var(--text-secondary)")}>{r.status}</span></td>
                    <td style={tdStyle}>
                      <select style={{ ...selectStyle, minHeight: 36, minWidth: 180 }} value={r.driver_id ?? ""} onChange={(e) => assign(r.id, e.target.value)} disabled={!["PLANNED", "ASSIGNED"].includes(r.status)}>
                        <option value="">— sin chofer —</option>
                        {drivers.map((d) => <option key={d.id} value={d.id}>{driverName(d)}</option>)}
                      </select>
                    </td>
                    <td style={tdStyle}>{c ? <span style={badge(c.color)}>{c.label}</span> : <span style={smallMutedStyle}>—</span>}</td>
                    <td style={tdStyle}><Button variant="secondary" onClick={() => navigate(`/admin/recorridos?date=${r.scheduled_date}`)}>Ver</Button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {editing ? (
        <TemplateModal template={editing === "new" ? null : editing} stores={stores} drivers={drivers} req={req}
          onClose={() => setEditing(null)}
          onSaved={(name) => { setEditing(null); flash(`Ronda «${name}» guardada`); loadTemplates(); }} />
      ) : null}
    </div>
  );
}

function TemplateModal({ template, stores, drivers, req, onClose, onSaved }: { template: Template | null; stores: Store[]; drivers: Driver[]; req: (p: string, i?: RequestInit) => Promise<any>; onClose: () => void; onSaved: (name: string) => void }) {
  const [name, setName] = useState(template?.name ?? "");
  const [notes, setNotes] = useState(template?.notes ?? "");
  const [active, setActive] = useState(template?.active ?? true);
  const [stopIds, setStopIds] = useState<number[]>(template?.stops.map((s) => s.store_id) ?? []);
  const [departures, setDepartures] = useState<Departure[]>(template?.departures.map((d) => ({ ...d })) ?? [{ departure_time: "09:00", weekdays: [1, 2, 3, 4, 5], window_seq: 1, default_driver_id: null, active: true }]);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const storeById = useMemo(() => new Map(stores.map((s) => [s.id, s])), [stores]);
  const candidates = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return stores.filter((s) => !stopIds.includes(s.id) && (!f || s.nombre_fantasia.toLowerCase().includes(f) || String(s.codigo_postal ?? "").includes(f))).slice(0, 40);
  }, [stores, stopIds, filter]);

  const move = (i: number, dir: -1 | 1) => setStopIds((xs) => { const j = i + dir; if (j < 0 || j >= xs.length) return xs; const c = [...xs]; [c[i], c[j]] = [c[j], c[i]]; return c; });
  const setDep = (i: number, patch: Partial<Departure>) => setDepartures((ds) => ds.map((d, k) => (k === i ? { ...d, ...patch } : d)));
  // Lunes a viernes / sábado / domingo, como grupo: cada botón prende o apaga
  // todos los días que representa a la vez. weekdays sigue siendo el mismo
  // array de siempre, esto es sólo una forma más simple de completarlo.
  const toggleDayGroup = (i: number, days: readonly number[]) =>
    setDepartures((ds) => ds.map((d, k) => {
      if (k !== i) return d;
      const allOn = days.every((day) => d.weekdays.includes(day));
      const weekdays = allOn
        ? d.weekdays.filter((w) => !days.includes(w))
        : Array.from(new Set([...d.weekdays, ...days])).sort((a, b) => a - b);
      return { ...d, weekdays };
    }));

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const body = { name, notes: notes || null, active, stores: stopIds, departures: departures.map((d) => ({ id: d.id, departure_time: d.departure_time, weekdays: d.weekdays, window_seq: d.window_seq, default_driver_id: d.default_driver_id, active: d.active })) };
      if (template) await req(`/v2/route-templates/${template.id}`, { method: "PUT", body: JSON.stringify(body) });
      else await req("/v2/route-templates", { method: "POST", body: JSON.stringify(body) });
      onSaved(name);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <Modal title={template ? `Ronda «${template.name}»` : "Nueva ronda"} onClose={onClose}>
      {error ? <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)", marginBottom: 12 }}>{error}</div> : null}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "12px", marginBottom: 16 }}>
        <Input label="Nombre" value={name} onChange={(e) => setName(e.target.value)} placeholder="Norte 1" />
        <Input label="Notas" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="opcional" />
        {template ? <label style={{ display: "flex", alignItems: "center", gap: 8, alignSelf: "end", color: "var(--text-primary)" }}><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Ronda activa (se genera)</label> : null}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px", marginBottom: 16 }}>
        <div style={panelStyle}>
          <h4 style={{ margin: "0 0 8px", color: "var(--text-primary)" }}>Tiendas, en el orden de la ronda <span style={smallMutedStyle}>· {stopIds.length}</span></h4>
          {stopIds.length === 0 ? <div style={smallMutedStyle}>Agregá tiendas desde la lista de la derecha.</div> : null}
          {stopIds.map((id, i) => {
            const s = storeById.get(id);
            return (
              <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ ...smallMutedStyle, width: 22 }}>{i + 1}.</span>
                <span style={{ flex: 1, color: "var(--text-primary)" }}>{s?.nombre_fantasia ?? `Tienda ${id}`} <span style={smallMutedStyle}>{s?.codigo_postal ? `· CP ${s.codigo_postal}` : ""}</span></span>
                <button type="button" style={chip(false)} onClick={() => move(i, -1)} aria-label="subir">↑</button>
                <button type="button" style={chip(false)} onClick={() => move(i, 1)} aria-label="bajar">↓</button>
                <button type="button" style={chip(false)} onClick={() => setStopIds((xs) => xs.filter((x) => x !== id))} aria-label="quitar">×</button>
              </div>
            );
          })}
        </div>
        <div style={panelStyle}>
          <h4 style={{ margin: "0 0 8px", color: "var(--text-primary)" }}>Agregar tienda</h4>
          <Input label="Buscar" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="nombre o CP" />
          <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 8 }}>
            {candidates.map((s) => (
              <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ color: "var(--text-primary)" }}>{s.nombre_fantasia} <span style={smallMutedStyle}>{s.codigo_postal ? `· CP ${s.codigo_postal}` : ""}</span></span>
                <button type="button" style={chip(true)} onClick={() => setStopIds((xs) => [...xs, s.id])}>Agregar</button>
              </div>
            ))}
            {candidates.length === 0 ? <div style={smallMutedStyle}>Sin tiendas para ese filtro.</div> : null}
          </div>
        </div>
      </div>

      <div style={{ ...panelStyle, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h4 style={{ margin: 0, color: "var(--text-primary)" }}>Salidas <span style={smallMutedStyle}>· a veces se sale más de una vez por día</span></h4>
          <Button variant="secondary" onClick={() => setDepartures((ds) => [...ds, { departure_time: "17:00", weekdays: [1, 2, 3, 4, 5], window_seq: 2, default_driver_id: null, active: true }])}>Agregar salida</Button>
        </div>
        {departures.map((d, i) => (
          <div key={d.id ?? `n${i}`} style={{ display: "grid", gridTemplateColumns: "110px 1fr 110px 220px 40px", gap: 10, alignItems: "center", padding: "8px 0", borderTop: i ? "1px solid var(--border)" : undefined }}>
            <Input label="Hora" type="time" value={d.departure_time.slice(0, 5)} onChange={(e) => setDep(i, { departure_time: e.target.value })} />
            <div>
              <span style={{ ...smallMutedStyle, display: "block", marginBottom: 6, fontWeight: 600 }}>Días</span>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {DAY_GROUPS.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    style={chip(g.days.every((day) => d.weekdays.includes(day)))}
                    onClick={() => toggleDayGroup(i, g.days)}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
            <label style={{ display: "block" }}>
              <span style={{ ...smallMutedStyle, display: "block", marginBottom: 6, fontWeight: 600 }}>Pasada</span>
              <select style={{ ...selectStyle, minHeight: 40 }} value={d.window_seq} onChange={(e) => setDep(i, { window_seq: Number(e.target.value) })}><option value={1}>1ª</option><option value={2}>2ª</option><option value={3}>3ª</option></select>
            </label>
            <label style={{ display: "block" }}>
              <span style={{ ...smallMutedStyle, display: "block", marginBottom: 6, fontWeight: 600 }}>Chofer habitual</span>
              <select style={{ ...selectStyle, minHeight: 40 }} value={d.default_driver_id ?? ""} onChange={(e) => setDep(i, { default_driver_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">— se asigna cada día —</option>
                {drivers.map((x) => <option key={x.id} value={x.id}>{driverName(x)} · {x.username}</option>)}
              </select>
            </label>
            <button type="button" style={{ ...chip(false), alignSelf: "end" }} onClick={() => setDepartures((ds) => ds.filter((_, k) => k !== i))} aria-label="quitar salida" disabled={departures.length === 1}>×</button>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button onClick={save} disabled={saving || !name.trim() || stopIds.length === 0}>{saving ? "Guardando…" : "Guardar ronda"}</Button>
      </div>
    </Modal>
  );
}
